/**
 * =================================================================
 * LE BON INDICATIF POUR UN NUMÉRO DES DOM (29/10/2026)
 *
 * Le mobile du technicien arrive de l'IBM i au format NATIONAL (« 0696 12 34 56 »).
 * Le champ de saisie, lui, veut du E.164 et partait de la Guadeloupe (« +590 ») :
 * « +590 696… » n'est pas un numéro valide — les mobiles de la Martinique sont en
 * +596 696 / +596 697 — donc la page répondait « Le numéro de téléphone portable est
 * invalide » à l'administrateur, tant qu'il n'avait pas changé le drapeau à la main.
 *
 * Règle (celle du serveur, décision ARCEP 00-535, ARCEP Mayotte 2007) : un numéro
 * national de 10 chiffres dont la série mobile appartient à un territoire d'outre-mer
 * prend l'indicatif DE CE TERRITOIRE ; la métropole garde +33 ; tout le reste est
 * refusé (null) plutôt que deviné.
 * =================================================================
 */

import type { Country } from 'react-phone-number-input';

/** Les pays que cet écran sait déduire — sous-ensemble du `Country` attendu par PhoneInput. */
export type PaysTelephone = Extract<Country, 'GP' | 'GF' | 'MQ' | 'YT' | 'RE' | 'FR'>;

/** Séries mobiles nationales d'outre-mer : préfixe à 4 chiffres → indicatif du territoire. */
const SERIES_DOM: Array<{ prefixe: string; indicatif: string }> = [
  { prefixe: '0690', indicatif: '+590' },  // Guadeloupe, Saint-Martin, Saint-Barthélemy
  { prefixe: '0691', indicatif: '+590' },  // Guadeloupe
  { prefixe: '0694', indicatif: '+594' },  // Guyane
  { prefixe: '0696', indicatif: '+596' },  // Martinique
  { prefixe: '0697', indicatif: '+596' },  // Martinique
  { prefixe: '0692', indicatif: '+262' },  // La Réunion
  { prefixe: '0693', indicatif: '+262' },  // La Réunion
  { prefixe: '0639', indicatif: '+262' }   // Mayotte
];

/**
 * Le numéro sous sa forme E.164, ou `null` s'il n'y a rien à en tirer.
 * Nettoie espaces, points, tirets et parenthèses ; laisse un E.164 tel quel.
 */
export function versE164(brut: string | null | undefined): string | null {
  const nettoye = String(brut ?? '').replace(/[\s.\-()]/g, '');
  if (!nettoye) return null;

  // Déjà international : « + » puis 7 à 15 chiffres (E.164), on n'y touche pas
  if (/^\+\d{7,15}$/.test(nettoye)) return nettoye;

  // National : exactement 10 chiffres, commençant par 0, sans « + »
  if (!/^0\d{9}$/.test(nettoye)) return null;

  for (const { prefixe, indicatif } of SERIES_DOM) {
    if (nettoye.startsWith(prefixe)) return indicatif + nettoye.slice(1);
  }

  // Séries mobiles de métropole (06, 07) ; le reste (fixes, 08, 09) ne reçoit pas de SMS
  if (nettoye.startsWith('06') || nettoye.startsWith('07')) return '+33' + nettoye.slice(1);

  return null;
}

/** Le pays à présenter dans le champ de saisie, d'après l'indicatif du numéro. */
export function paysDuNumero(e164: string | null | undefined): PaysTelephone | undefined {
  const numero = String(e164 ?? '').trim();
  if (!numero) return undefined;
  if (numero.startsWith('+590')) return 'GP';
  if (numero.startsWith('+594')) return 'GF';
  if (numero.startsWith('+596')) return 'MQ';
  if (numero.startsWith('+262639')) return 'YT'; // Mayotte est dans l'indicatif +262
  if (numero.startsWith('+262')) return 'RE';
  if (numero.startsWith('+33')) return 'FR';
  return undefined;
}
